import { useEffect, useMemo, useRef } from 'react';
import { t } from 'i18next';
import { buildWorkspaceCustomFontsUrlsMap, useWorkflowFontsQuery } from '@/queries/useWorkflowFonts';
import { useWorkspaceFontsQuery } from '@/queries/useWorkspaceFonts';
import { FF_WORKSPACE_CUSTOM_FONTS } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { I18N_KEYS } from '@/language/keys';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import useWorkspacesStore from '@/state/workspaces.state';
import { encodeFontStyleValue, makeWorkspaceFontFaceKey, type FontStyleCombination } from '@/utils/customFonts.utils';
import type { UseQueryResult } from '@tanstack/react-query';
import type { WorkspaceCustomFontDto } from '@/api/recipes';

function workspaceFontsShellLoadingState(
  queryEnabled: boolean,
  workspaceId: string | undefined,
  workflowFonts: Pick<UseQueryResult<WorkspaceCustomFontDto[], Error>, 'isError' | 'isSuccess'>,
  userWorkspaceFonts: Pick<UseQueryResult<WorkspaceCustomFontDto[], Error>, 'isError' | 'isSuccess'>,
): 'loading' | 'loaded' | 'error' {
  if (!queryEnabled) {
    return 'loaded';
  }
  if (!workspaceId) {
    return 'loading';
  }
  if (workflowFonts.isError || userWorkspaceFonts.isError) {
    return 'error';
  }
  if (workflowFonts.isSuccess && userWorkspaceFonts.isSuccess) {
    return 'loaded';
  }
  return 'loading';
}

function buildWorkspaceCustomFontFaceKeySet(fonts: WorkspaceCustomFontDto[]): ReadonlySet<string> {
  const s = new Set<string>();
  for (const f of fonts) {
    s.add(makeWorkspaceFontFaceKey(f.fontFamilyKey, f.weight, f.isItalic));
  }
  return s;
}

/** Distinct (weight, italic) faces per `fontFamilyKey` from workspace font rows. */
function buildWorkspaceFontStyleCombinationsByFamily(
  fonts: WorkspaceCustomFontDto[],
): ReadonlyMap<string, FontStyleCombination[]> {
  const byFamily = new Map<string, FontStyleCombination[]>();
  const seenKeys = new Map<string, Set<string>>();
  for (const f of fonts) {
    const enc = encodeFontStyleValue(f.weight, f.isItalic);
    let seen = seenKeys.get(f.fontFamilyKey);
    if (!seen) {
      seen = new Set();
      seenKeys.set(f.fontFamilyKey, seen);
      byFamily.set(f.fontFamilyKey, []);
    }
    if (seen.has(enc)) {
      continue;
    }
    seen.add(enc);
    byFamily.get(f.fontFamilyKey)!.push({ weight: f.weight, isItalic: f.isItalic });
  }
  for (const combos of byFamily.values()) {
    combos.sort((a, b) => (a.weight !== b.weight ? a.weight - b.weight : Number(a.isItalic) - Number(b.isItalic)));
  }
  return byFamily;
}

/**
 * Loads custom font metadata for the workflow shell:
 * - Presigned URL map from {@link useWorkflowFontsQuery} (workflow's workspace; same as graph asset hosting).
 * - Allowed face keys and per-family style combinations from {@link useWorkspaceFontsQuery} (active user workspace; drives missing-font detection and replacement UI).
 */
export function useCustomFontsInfo(workflowId: string | undefined) {
  const { value: isWorkspaceCustomFontsEnabled } = useFeatureFlag(FF_WORKSPACE_CUSTOM_FONTS);
  const workspaceId = useWorkspacesStore((s) => s.activeWorkspace.workspaceId);
  const setLoadingState = useWorkflowStore((s) => s.setLoadingState);
  const queryEnabled = Boolean(workflowId) && isWorkspaceCustomFontsEnabled === true;
  const userFontsQueryEnabled = queryEnabled && Boolean(workspaceId);

  const workflowFontsQuery = useWorkflowFontsQuery(workflowId, { enabled: queryEnabled });
  const userWorkspaceFontsQuery = useWorkspaceFontsQuery(workspaceId, { enabled: userFontsQueryEnabled });

  const {
    data: workflowFontsData,
    isError: workflowFontsIsError,
    isSuccess: workflowFontsIsSuccess,
    errorUpdatedAt: workflowFontsErrorUpdatedAt,
  } = workflowFontsQuery;
  const {
    data: userWorkspaceFontsData,
    isError: userWorkspaceFontsIsError,
    isSuccess: userWorkspaceFontsIsSuccess,
    errorUpdatedAt: userWorkspaceFontsErrorUpdatedAt,
  } = userWorkspaceFontsQuery;

  const lastSnackbarErrorUpdatedAt = useRef(0);

  useEffect(() => {
    setLoadingState(
      'workspace-fonts',
      workspaceFontsShellLoadingState(
        queryEnabled,
        workspaceId,
        { isError: workflowFontsIsError, isSuccess: workflowFontsIsSuccess },
        { isError: userWorkspaceFontsIsError, isSuccess: userWorkspaceFontsIsSuccess },
      ),
    );

    const combinedErrorUpdatedAt = Math.max(workflowFontsErrorUpdatedAt ?? 0, userWorkspaceFontsErrorUpdatedAt ?? 0);
    const anyFontQueryError =
      queryEnabled && (workflowFontsIsError || (userFontsQueryEnabled && userWorkspaceFontsIsError));

    if (!anyFontQueryError) {
      lastSnackbarErrorUpdatedAt.current = 0;
      return;
    }
    if (combinedErrorUpdatedAt === lastSnackbarErrorUpdatedAt.current) {
      return;
    }
    lastSnackbarErrorUpdatedAt.current = combinedErrorUpdatedAt;
    useGlobalStore.getState().updateSnackbarData({
      text: t(I18N_KEYS.FLOW_WARNINGS.FAILED_LOAD_WORKSPACE_CUSTOM_FONTS),
      icon: null,
      isOpen: true,
      duration: 5000,
      severity: 'warning',
    });
  }, [
    queryEnabled,
    userFontsQueryEnabled,
    workflowFontsIsError,
    workflowFontsIsSuccess,
    workflowFontsErrorUpdatedAt,
    userWorkspaceFontsIsError,
    userWorkspaceFontsIsSuccess,
    userWorkspaceFontsErrorUpdatedAt,
    setLoadingState,
    workspaceId,
  ]);

  const workflowCustomFontsUrlsMap = useMemo(
    () => buildWorkspaceCustomFontsUrlsMap(workflowFontsData ?? []),
    [workflowFontsData],
  );
  const workspaceCustomFontFaceKeys = useMemo(
    () => buildWorkspaceCustomFontFaceKeySet(userWorkspaceFontsData ?? []),
    [userWorkspaceFontsData],
  );
  const workspaceFontStyleCombinationsByFamily = useMemo(
    () => buildWorkspaceFontStyleCombinationsByFamily(userWorkspaceFontsData ?? []),
    [userWorkspaceFontsData],
  );

  return {
    workflowCustomFontsUrlsMap,
    workspaceCustomFontFaceKeys,
    workspaceFontStyleCombinationsByFamily,
  };
}
